import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import ElementComponent from '../../../Component/UI/Element';
import Renderable from '../../../Component/Renderable';
import { Easing } from '@tweenjs/tween.js';
import { PlayAnimation, AwaitIterators } from '../AnimationHelper';
import UIImageComponent from '../../../Component/UI/Image';
import {
    StarChestEvent, StarChestHelper
} from '../../../Utility/StarChestHelper';
import { tVector4 } from '../../../Utility/ArrayHelper';
import Game from '../../../Game';
import { StarChestUpgrade } from './StarChestUpgrade';

export interface ProgressBarChestUpgradeParams {
    tier: number;
    progressBar: GameObject;
    starChest: GameObject;
    progressBarLeftIcon: GameObject;
    progressBarRightIcon: GameObject;
}

const longDuration = 1000;
const shortDuration = 500;

export class ProgressBarChestUpgrade extends AnimationClip<
    ProgressBarChestUpgradeParams
> {
    public *execute(
        gameObject: GameObject,
        meta: ProgressBarChestUpgradeParams
    ): IterableIterator<number> {
        const containerRenderable = gameObject.getComponent(Renderable);
        const containerElement = gameObject.getComponent(ElementComponent);
        const originalScale = containerElement.scale.clone();

        const initialChestOffsets = [...containerElement.offsets] as tVector4;

        //

        containerRenderable.visible = true;

        // Focus on chest

        // Prepare tween to move chest to center
        const currentChestOffsetTween = this.moveChestTo(containerElement, [
            0,
            -300,
            0,
            -300,
        ]);

        // Prepare tween to scale the chest up as it moves to center
        const currentChestTweenScale = this.getTween(containerElement.scale)
            .to({ x: 3, y: 3 }, longDuration)
            .easing(Easing.Linear.None);

        // Prepare tween to fade out the progress bar
        const progressBarRenderable = meta.progressBar.getComponent(Renderable);
        const progressBarAlphaTween = this.getTween(progressBarRenderable).to(
            { alpha: 0 },
            longDuration
        );

        // Run tweens to focus on the chest
        yield* AwaitIterators([
            this.startTween(currentChestTweenScale),
            this.startTween(progressBarAlphaTween),
            this.startTween(currentChestOffsetTween),
        ]);

        // Play upgrade animation

        Game.scene.getActiveScene().emit(StarChestEvent.UPGRADE_CHEST, {
            tier: meta.tier,
        });

        yield* PlayAnimation(
            new StarChestUpgrade(meta.starChest, {
                tier: meta.tier,
            })
        );

        // Update chest textures
        this.updateChestTexture(
            meta.progressBarLeftIcon,
            StarChestHelper.getCurrentOrPreviousTier(meta.tier)
        );
        this.updateChestTexture(
            meta.progressBarRightIcon,
            StarChestHelper.getNextOrLastTier(meta.tier)
        );

        // Unfocus chest

        const unfocusTweens = [
            // Prepare tween to move chest back
            this.moveChestTo(containerElement, initialChestOffsets),
            // Prepare tween to scale chest back as it moves back
            currentChestTweenScale
                .to(originalScale, longDuration)
                .easing(Easing.Linear.None),
        ];

        // Optionally fade the progress bar back in
        if (StarChestHelper.tierExists(meta.tier + 1)) {
            unfocusTweens.push(
                progressBarAlphaTween.to({ alpha: 1 }, shortDuration)
            );
        }

        // Run tweens to unfocus on the chest
        yield* AwaitIterators(
            unfocusTweens.map(tween => this.startTween(tween))
        );

        // Finally, fade out the chest that was in focus
        const currentChestAlphaTween = this.getTween(containerRenderable);
        currentChestAlphaTween.to({ alpha: 0 }, shortDuration);
        yield* this.startTween(currentChestAlphaTween);
        containerRenderable.alpha = 1;

        //

        containerRenderable.visible = false;
    }

    private moveChestTo(containerElement: ElementComponent, offsets: tVector4) {
        return this.getTween(containerElement.offsets)
            .to(
                {
                    0: offsets[0],
                    1: offsets[1],
                    2: offsets[2],
                    3: offsets[3],
                },
                longDuration
            )
            .easing(Easing.Quadratic.Out)
            .onUpdate(t => {
                containerElement.offsets = t;
            });
    }

    private updateChestTexture(chest: GameObject, tier: number) {
        const texture = StarChestHelper.getStarChestProgressBarTexture(tier);
        chest.getComponent(UIImageComponent).texture = texture;
    }
}
